import React, { Suspense, useEffect, useCallback, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/common/ErrorBoundary';
import MainLayout from './components/Layout/MainLayout';
import { appRoutes } from './routes/routeConfig';
import { APP_BASE_PATH } from './utils/constants';
import { initAnalytics, trackPageView } from './utils/analytics';
import { ApplicationProvider } from './context/ApplicationContext';
import { AuthPromptProvider } from './context/AuthPromptContext';
const RouteTracker = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [showShortcutHelp, setShowShortcutHelp] = useState(false);
    useEffect(() => {
        trackPageView(location.pathname + location.search);
    }, [location]);
    const handleKeyDown = useCallback((e) => {
        const normalizedKey = String(e.key || '').toLowerCase();
        const isSearchShortcut = normalizedKey === 'k' || e.code === 'KeyK';
        // Ctrl+K or Cmd+K to focus search
        if ((e.ctrlKey || e.metaKey) && isSearchShortcut) {
            e.preventDefault();
            navigate('/search');
            return;
        }
        // Escape to close help dialog
        if (e.key === 'Escape' && showShortcutHelp) {
            setShowShortcutHelp(false);
            return;
        }
        // Skip remaining shortcuts when typing in input fields
        if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target?.tagName))
            return;
        if (e.target?.isContentEditable)
            return;
        // "/" to focus search (only when not typing in an input)
        if (e.key === '/') {
            e.preventDefault();
            navigate('/search');
            return;
        }
        // "?" to toggle keyboard shortcut help
        if (e.key === '?') {
            e.preventDefault();
            setShowShortcutHelp((prev) => !prev);
        }
    }, [navigate, showShortcutHelp]);
    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);
    if (!showShortcutHelp)
        return null;
    return (React.createElement("div", { className: "modal", style: { display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }, onClick: () => setShowShortcutHelp(false), role: "dialog", "aria-label": "Keyboard shortcuts" },
        React.createElement("div", { className: "modal-dialog", onClick: (e) => e.stopPropagation() },
            React.createElement("div", { className: "modal-content" },
                React.createElement("div", { className: "modal-header" },
                    React.createElement("button", { type: "button", className: "close", onClick: () => setShowShortcutHelp(false), "aria-label": "Close" }, "\u00D7"),
                    React.createElement("h4", { className: "modal-title" },
                        React.createElement("i", { className: "fa fa-keyboard-o" }),
                        " Keyboard Shortcuts")),
                React.createElement("div", { className: "modal-body" },
                    React.createElement("table", { className: "table table-condensed" },
                        React.createElement("tbody", null,
                            React.createElement("tr", null,
                                React.createElement("td", null,
                                    React.createElement("kbd", null, "/")),
                                React.createElement("td", null, "Focus search")),
                            React.createElement("tr", null,
                                React.createElement("td", null,
                                    React.createElement("kbd", null, "Ctrl"),
                                    "+",
                                    React.createElement("kbd", null, "K")),
                                React.createElement("td", null, "Focus search")),
                            React.createElement("tr", null,
                                React.createElement("td", null,
                                    React.createElement("kbd", null, "?")),
                                React.createElement("td", null, "Show this help")),
                            React.createElement("tr", null,
                                React.createElement("td", null,
                                    React.createElement("kbd", null, "Esc")),
                                React.createElement("td", null, "Close dialog / Clear search")),
                            React.createElement("tr", null,
                                React.createElement("td", null,
                                    React.createElement("kbd", null, "\u2191"),
                                    " ",
                                    React.createElement("kbd", null, "\u2193")),
                                React.createElement("td", null, "Navigate search results")),
                            React.createElement("tr", null,
                                React.createElement("td", null,
                                    React.createElement("kbd", null, "Enter")),
                                React.createElement("td", null, "Open selected result")))))))));
};
const App = () => {
    useEffect(() => {
        initAnalytics();
    }, []);
    const resolvedBasePath = typeof window !== 'undefined' &&
        (window.location.pathname === APP_BASE_PATH || window.location.pathname.startsWith(`${APP_BASE_PATH}/`))
        ? APP_BASE_PATH
        : '';
    return (React.createElement(Router, { basename: resolvedBasePath },
        React.createElement(ApplicationProvider, null,
            React.createElement(AuthPromptProvider, null,
                React.createElement(RouteTracker, null),
                React.createElement(MainLayout, null,
                    React.createElement(ErrorBoundary, null,
                        React.createElement(Suspense, { fallback: React.createElement(LoadingSpinner, null) },
                            React.createElement(Routes, null, appRoutes.map((route) => (React.createElement(Route, { key: route.path, path: route.path, element: route.element })))))))))));
};
export default App;
